import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";

const SLUG = "montessori-waldorf-reggio";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Montessori vs. Waldorf vs. Reggio Emilia — Kolme pedagogiikkaa vertailussa";
const DESC = "Montessori, Waldorf ja Reggio Emilia ovat maailman tunnetuimmat vaihtoehtopedagogiikat. Vertailu juurista, ympäristöstä, materiaaleista ja arjesta.";
const PUBLISHED = "2026-07-16";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/montessori-waldorf-reggio")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Vertailu"
      title={TITLE}
      lead="Montessori, Waldorf ja Reggio Emilia ovat maailman kolme tunnetuinta vaihtoehtopedagogiikkaa. Kaikki kunnioittavat lasta, mutta ne rakentavat ympäristön ja päivän eri tavoin. Tämä artikkeli avaa juuret, käytännön eron ja sen, mitä kotivanhempi voi oppia jokaiselta."
      readingMinutes={9}
      publishedISO={PUBLISHED}
    >
      <h2>Kolme koulukuntaa, kolme aikakautta</h2>
      <ul>
        <li><strong>Montessori</strong> — italialainen lääkäri Maria Montessori, Rooma 1907.</li>
        <li><strong>Waldorf</strong> — itävaltalainen filosofi Rudolf Steiner, Stuttgart 1919.</li>
        <li><strong>Reggio Emilia</strong> — italialainen opettaja Loris Malaguzzi, Reggio Emilia 1945.</li>
      </ul>
      <p>Kaikki syntyivät murroksesta: Montessori köyhäinkortteleiden uudistuksesta, Waldorf ensimmäisen maailmansodan jälkeen työläisten lapsille, Reggio Emilia toisen maailmansodan raunioista. Kaikki kolme torjuivat aikansa mekaanisen opetuksen ja asettivat lapsen keskiöön — mutta hyvin eri tavoin.</p>

      <h2>Montessori — valmisteltu ympäristö</h2>
      <p>Montessorissa ympäristö on tarkkaan mietitty. Materiaalit ovat pedagogisesti suunniteltuja ja opettavat yhden asian kerrallaan: pinkki torni kokoja, punaiset tangot pituuksia, kirjaimet karkealla paperilla muotoa ja äännettä. Lapsi valitsee itse työn, toistaa sen niin monta kertaa kuin haluaa ja palauttaa hyllyyn.</p>
      <p>Aikuinen valmistelee ympäristön ja tarkkailee. Ryhmät ovat monikerroksisia — 3–6-vuotiaat samassa tilassa, jolloin nuorempi oppii vanhemmalta. Fantasialeikkiä suositaan vähemmän kuin todellisia töitä: lapsi pesee omat astiansa ja leipoo aitoa leipää.</p>

      <h2>Waldorf — mielikuvitus ja rytmi</h2>
      <p>Waldorf perustuu Rudolf Steinerin antroposofiaan. Ajatuksena on, että lapsen kehitys jakautuu seitsenvuotisiin kausiin, ja pieni lapsi oppii ennen kaikkea jäljittelemällä. Ympäristö on lämmin, pehmeä ja täynnä luonnonmateriaaleja: villakangasta, silkkiä, koivunkeppejä ja viimeistelemättömiä puuleluja.</p>
      <p>Waldorfissa on <strong>vahva päivärytmi</strong>: samat toiminnot toistuvat samaan aikaan joka päivä. Sadut, laulut ja käsityö kulkevat viikoittain. Kuvitteluleikki on keskiössä, kirjaimia ja numeroita ei opeteta ennen 7-vuotiaana. Näytöt ja sähköinen media pidetään pienten lasten arjesta poissa.</p>

      <h2>Reggio Emilia — lapsi tutkijana ja taiteilijana</h2>
      <p>Reggio Emilia syntyi kansanliikkeenä sodan jälkeen: vanhemmat rakensivat itse päiväkotinsa. Loris Malaguzzi kokosi ajatukset "sadan kielen" filosofiaksi — lapsi ilmaisee itseään sadan eri "kielen" kautta: piirtäen, savella, valolla, kehollaan, sanoin.</p>
      <p>Reggio Emiliassa on <strong>projektityö keskeistä</strong>. Aikuiset seuraavat, mitä lapset kysyvät, ja rakentavat siitä pitkiä yhteisiä tutkimusprojekteja. Ympäristö on esteettinen ja täynnä luonnonvaloa, ja jokaisessa yksikössä on oma <em>atelier</em> — taidepaja — ja <em>atelierista</em>, taidepedagogi.</p>

      <h2>Suora vertailu</h2>
      <table>
        <thead>
          <tr>
            <th>Ominaisuus</th>
            <th>Montessori</th>
            <th>Waldorf</th>
            <th>Reggio Emilia</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Perustaja</strong></td>
            <td>Maria Montessori (1907)</td>
            <td>Rudolf Steiner (1919)</td>
            <td>Loris Malaguzzi (1945)</td>
          </tr>
          <tr>
            <td><strong>Ympäristö</strong></td>
            <td>Strukturoitu, avoimet hyllyt, pedagogiset materiaalit</td>
            <td>Lämmin, pehmeä, viimeistelemättömät luonnonmateriaalit</td>
            <td>Esteettinen studio, taidevälineet, luonnonvalo</td>
          </tr>
          <tr>
            <td><strong>Oppimisen muoto</strong></td>
            <td>Itsenäinen työ, toisto, aistimateriaalit</td>
            <td>Jäljittely, sadut, laulu, käsityö, vahva rytmi</td>
            <td>Projektit, tutkiminen, taide, dokumentointi</td>
          </tr>
          <tr>
            <td><strong>Fantasia vs. todellisuus</strong></td>
            <td>Todelliset työt, fantasia toissijainen alle 6 v</td>
            <td>Fantasia ja mielikuvitus keskiössä</td>
            <td>Lapsen omat teoriat ja tutkimukset keskiössä</td>
          </tr>
          <tr>
            <td><strong>Lukemaan opettaminen</strong></td>
            <td>Kun lapsi osoittaa kiinnostusta, usein 4–5 v</td>
            <td>Vasta noin 7 v iässä</td>
            <td>Kirjoitus syntyy projekteista, ei erikseen opeteta</td>
          </tr>
          <tr>
            <td><strong>Aikuisen rooli</strong></td>
            <td>Ohjaaja, joka valmistelee ympäristön</td>
            <td>Esikuva, jota lapsi jäljittelee</td>
            <td>Kumppani ja tutkimuspari, joka dokumentoi</td>
          </tr>
          <tr>
            <td><strong>Kotisovellus</strong></td>
            <td>Avoimet hyllyt, oppimistorni, aidot työt</td>
            <td>Luonnonmateriaalit, vahva päivärytmi, sadut</td>
            <td>Taidenurkkaus, avoimet materiaalit, dokumentointi</td>
          </tr>
        </tbody>
      </table>

      <h2>Mitä kotivanhempi voi oppia kaikilta kolmelta?</h2>
      <ul>
        <li><strong>Montessorilta:</strong> lapsen mittakaava, valmisteltu ympäristö, avoimet hyllyt ja aidot työt. Anna lapsen kaataa oma maitolasi.</li>
        <li><strong>Waldorfilta:</strong> päivärytmi ja rauha. Sama nukkumaanmenoseremonia joka ilta, viikoittainen leivontapäivä, satu ennen unia.</li>
        <li><strong>Reggio Emilialta:</strong> lapsen kysymysten kuunteleminen. Kun lapsi kysyy "mistä sade tulee?", älä anna vastausta — tutkikaa yhdessä.</li>
      </ul>
      <p>Useimmat suomalaiset kodit yhdistävät piirteitä kaikista kolmesta. Se on täysin sallittua — pedagogiikat eivät ole uskontoja, vaan työkalupakkeja.</p>

      <h2>Suomessa</h2>
      <p>Suomessa toimivat kaikki kolme suuntausta: Montessori-päiväkoteja on kymmeniä, steinerkouluja (Waldorf) noin 25 ympäri maata, ja Reggio Emilia -henkinen työskentely on levinnyt monissa kunnallisissakin päiväkodeissa. Suomalainen varhaiskasvatuslaki tukee kaikkien näiden suuntausten arvoja: lapsen osallisuutta, leikkiä ja yksilöllistä tukea.</p>

      <h2>Yhteenveto</h2>
      <p>Montessori, Waldorf ja Reggio Emilia eivät ole toistensa vastakohtia. Ne ovat kolme tapaa vastata samaan kysymykseen: <em>miten aikuinen voi tukea lapsen kasvua ilman että astuu tielle?</em> Valinta perheelle riippuu arvoista ja lapsen luonteesta — mutta parhaimmillaan koti ammentaa kaikista kolmesta.</p>
      <p>Haluatko rakentaa lapsen mittakaavaisen ympäristön kotiin? <Link to="/kauppa">Selaa kalusteita →</Link> tai lue <Link to="/montessori-koulu/montessori-hei-vertailu">Montessori vs. HEI Schools -vertailumme</Link>.</p>
    </BlogPost>
  );
}
